import React from 'react';
import styled from 'styled-components';
import Button from 'components/UI/Button';
import Input from 'components/UI/Input';
import Spinner from 'components/UI/Spinner';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import Icon from 'view/Icon';
import { Border, Main, Text } from 'styles/themeColors';
import { Space } from 'styles/themeSpaces';
import { AFFILIATE_FORM_LABELS, AffiliateFormRegion, REGIONS } from 'constants/affiliateForm';

const Panel = styled.div`
  flex: 1;
  padding: ${Space.sp2x};
  box-sizing: border-box;
`;

const PanelDivider = styled.div`
  width: 1px;
  background-color: ${Border.light};
`;

const RegionTitle = styled.h4`
  margin: 0 0 ${Space.spBase} 0;
  color: ${Text.primary};
  font-size: 16px;
`;

const Row = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${Space.spBase};
`;

const InputWrapper = styled.div`
  flex: 1;
  min-width: 0;
`;

const Hint = styled.p`
  margin: ${Space.spBase} 0 0 0;
  color: ${Text.secondary};
  font-size: 12px;
  line-height: 16px;
`;

const StatusRow = styled(FlexboxContainer)`
  margin-top: ${Space.spBase};
  gap: ${Space.spHalf};
  min-height: 20px;
`;

const StatusText = styled.span<{ $tone: 'success' | 'error' | 'neutral' }>`
  font-size: 13px;
  color: ${({ $tone }) =>
    $tone === 'success' ? Main.success : $tone === 'error' ? Main.error : Text.secondary};
`;

export interface Props {
  region: AffiliateFormRegion;
  value: string;
  /** Non-null while this region's candidate id is being loaded. */
  pendingFormId: string | null;
  errorMessage: string;
  isLoaded: boolean;
  disabled: boolean;
  onChange: (region: AffiliateFormRegion, value: string) => void;
  onApply: (region: AffiliateFormRegion) => void;
}

const RegionFormConfig = ({
  region,
  value,
  pendingFormId,
  errorMessage,
  isLoaded,
  disabled,
  onChange,
  onApply
}: Props) => {
  const isApplying = pendingFormId !== null;
  // AC 4 — while a region is being applied its own field and button are disabled; the other
  // region is driven by its own state and stays usable.
  const controlsDisabled = disabled || isApplying;

  const renderStatus = () => {
    if (isApplying) {
      return (
        <>
          <Spinner size={14} />
          <StatusText $tone="neutral">{AFFILIATE_FORM_LABELS.APPLYING_STATUS}</StatusText>
        </>
      );
    }

    if (errorMessage) {
      return (
        <>
          <Icon name="alert-circle" size={16} fillColor={Main.error} />
          <StatusText $tone="error">{errorMessage}</StatusText>
        </>
      );
    }

    if (isLoaded) {
      return (
        <>
          <Icon name="check-circle" size={16} fillColor={Main.success} />
          <StatusText $tone="success">{AFFILIATE_FORM_LABELS.PREVIEW_LOADED}</StatusText>
        </>
      );
    }

    return null;
  };

  return (
    <Panel>
      <RegionTitle>{REGIONS[region].label}</RegionTitle>
      <Row>
        <InputWrapper>
          <Input
            id={`cognito-form-id-${region}`}
            labelName={AFFILIATE_FORM_LABELS.FORM_ID_LABEL}
            placeholder={AFFILIATE_FORM_LABELS.FORM_ID_PLACEHOLDER}
            value={value}
            onInputChange={(next: string) => onChange(region, next)}
            onEnter={() => !controlsDisabled && onApply(region)}
            disabled={controlsDisabled}
            isOptional
            showOptional={false}
            width="100%"
          />
        </InputWrapper>
        <Button
          id={`apply-cognito-form-${region}`}
          onClick={() => onApply(region)}
          disabled={controlsDisabled}
          bgColor={Main.brandDark}
          borderColor={Main.brandDark}
          width="140px"
        >
          {AFFILIATE_FORM_LABELS.APPLY}
        </Button>
      </Row>
      <StatusRow alignItems="center">{renderStatus()}</StatusRow>
      <Hint>{AFFILIATE_FORM_LABELS.REPLACE_HINT}</Hint>
    </Panel>
  );
};

export { PanelDivider };
export default RegionFormConfig;
